// The invoice row's "···" sheet on the mobile Finance list (mobile overhaul
// Phase C §8.1 A) — and the visible home of everything the row's swipe
// offers, so no action is gesture-only (decision 6).
//
// It is a hand-rolled `Sheet` of `SheetRow`s rather than a `PageMenu` on
// purpose: `PageMenu` dismisses itself before running the item's handler, so
// an item that opens a confirm would be unmounted by the parent first. Owning
// the step here keeps the menu → confirm hand-off in one component.
//
// Every action does exactly what the desktop InvoiceDetail header does:
// record payment and void are status PATCHes, the reminder is a POST that
// logs a timeline entry (no email), trash is the rail's DELETE.

import { useState } from 'react';
import { api } from '../lib/api';
import { reportError } from '../lib/errorEvents';
import { invoiceActionsFor } from '../lib/invoiceActions';
import { Sheet, SheetRow } from './Sheet';
import type { InvoiceStatus } from '../../shared/types';

/** "Send reminder" from the row swipe and from the sheet — one call site.
    Throws on failure so each caller decides what a failed reminder looks
    like: the sheet's `run` keeps it open and reports; the swipe reports
    instead of refetching as if it worked (review C-7). */
export async function logInvoiceReminder(id: string): Promise<void> {
  await api(`/api/admin/finance/invoices/${id}/reminder`, { method: 'POST' });
}

export interface InvoiceRowTarget {
  id: string;
  /** Already formatted — "#0244". */
  number: string;
  /** Sub-line under the sheet title. */
  context: string;
  /** Display status (a sent invoice past its due date reads as overdue). */
  status: InvoiceStatus;
}

export function InvoiceRowSheet({
  invoice,
  onOpen,
  onClose,
  onChanged,
}: {
  invoice: InvoiceRowTarget;
  onOpen: () => void;
  onClose: () => void;
  /** Refetch the list after a mutation. */
  onChanged: () => void;
}) {
  const [step, setStep] = useState<'menu' | 'void' | 'trash'>('menu');
  const [busy, setBusy] = useState(false);
  const allowed = new Set(invoiceActionsFor(invoice.status));

  const run = async (work: () => Promise<unknown>, message: string) => {
    if (busy) return;
    setBusy(true);
    try {
      await work();
      onChanged();
      onClose();
    } catch (error) {
      reportError(error, message);
      setBusy(false);
    }
  };

  const patch = (status: InvoiceStatus, message: string) =>
    run(() => api(`/api/admin/finance/invoices/${invoice.id}`, { method: 'PATCH', json: { status } }), message);

  if (step !== 'menu') {
    const voiding = step === 'void';
    return (
      <Sheet
        open
        variant="half"
        onClose={() => setStep('menu')}
        title={voiding ? `Void ${invoice.number}?` : `Move ${invoice.number} to trash?`}
        context={invoice.context}
        footer={
          <>
            <button type="button" className="btn" onClick={() => setStep('menu')}>
              Cancel
            </button>
            <button
              type="button"
              className="btn danger"
              disabled={busy}
              onClick={() =>
                voiding
                  ? void patch('void', 'Could not void the invoice.')
                  : void run(
                      () => api(`/api/admin/finance/invoices/${invoice.id}`, { method: 'DELETE' }),
                      'Could not move the invoice to trash.',
                    )
              }
            >
              {voiding ? 'Void invoice' : 'Move to trash'}
            </button>
          </>
        }
      >
        <p className="fin-confirm">
          {voiding
            ? 'A voided invoice stops counting as outstanding and can no longer be sent or paid.'
            : 'Trashed invoices stay recoverable from Settings → Trash.'}
        </p>
      </Sheet>
    );
  }

  return (
    <Sheet
      open
      variant="half"
      onClose={onClose}
      title={`Invoice ${invoice.number}`}
      context={invoice.context}
      className="pmenu"
      ariaLabel={`Actions for invoice ${invoice.number}`}
    >
      <SheetRow label="Open invoice" role="menuitem" onSelect={onOpen} />
      {allowed.has('record-payment') && (
        <SheetRow
          label="Record payment"
          sub="Marks the invoice paid"
          role="menuitem"
          disabled={busy}
          onSelect={() => void patch('paid', 'Could not record the payment.')}
        />
      )}
      {allowed.has('send-reminder') && (
        <SheetRow
          label="Send reminder"
          sub="Logs a reminder on the timeline"
          role="menuitem"
          disabled={busy}
          onSelect={() => void run(() => logInvoiceReminder(invoice.id), 'Could not log the reminder.')}
        />
      )}
      {allowed.has('void') && (
        <SheetRow label="Void invoice" role="menuitem" disabled={busy} onSelect={() => setStep('void')} />
      )}
      <SheetRow label="Move to trash" tone="danger" role="menuitem" disabled={busy} onSelect={() => setStep('trash')} />
    </Sheet>
  );
}
